import { useState } from 'react';
import { useFieldArray } from 'react-hook-form';

import {
  DragAndDropEditorModalType,
  useDragAndDropEditorContext,
  useDragAndDropEditorModalsContext,
} from 'modules/dragAndDropEditor/context';
import { Modal, ModalHeader } from 'ui/modal';
import { MANAGE_CUSTOM_CATEGORIES_ID } from '../../constants';
import { useDirtyCategories } from './hooks/useDirtyCategories';
import { useProcessCategoryDeletion } from './hooks/useProcessCategoryDeletion';
import { ManageCustomCategoriesFooter } from './ManageCustomCategoriesFooter';
import { ManageCustomCategoriesForm } from './ManageCustomCategoriesForm';
import {
  ManageCustomCategoriesFormValues,
  withManageCustomCategoriesFormProvider,
} from './ManageCustomCategoriesFormProvider';
import { MANAGE_CUSTOM_CATEGORIES_FORM_FIELD_NAMES } from './ManageCustomCategoriesModal.constants';
import {
  CategoriesFieldName,
  ManageCustomCategoriesConfirmationType,
} from './ManageCustomCategoriesModal.types';

type ManageCustomCategoriesModalProps = {
  onCloseCallback: () => void;
};

export const ManageCustomCategoriesModal = withManageCustomCategoriesFormProvider(
  ({ onCloseCallback }: ManageCustomCategoriesModalProps) => {
    const { closeModal } = useDragAndDropEditorModalsContext();
    const { messageCategories } = useDragAndDropEditorContext();
    const dirtyCategories = useDirtyCategories();

    const [manageCustomCategoriesConfirmationType, setManageCustomCategoriesConfirmationType] =
      useState<ManageCustomCategoriesConfirmationType>(ManageCustomCategoriesConfirmationType.None);

    const fieldArray = useFieldArray<ManageCustomCategoriesFormValues, CategoriesFieldName>({
      name: MANAGE_CUSTOM_CATEGORIES_FORM_FIELD_NAMES.CATEGORIES,
    });

    const {
      categoryToDelete,
      processCategoryDeletion,
      resetCategoryDeletionProcess,
      initiateCategoryDeletionProcess,
    } = useProcessCategoryDeletion(fieldArray, setManageCustomCategoriesConfirmationType);

    const handleClose = () => {
      if (dirtyCategories?.hasUnsavedChanges) {
        setManageCustomCategoriesConfirmationType(
          ManageCustomCategoriesConfirmationType.UnsavedChanges,
        );
      } else {
        onCloseCallback();
        closeModal(DragAndDropEditorModalType.ManageCustomCategories);
      }
    };

    return (
      <Modal
        data-test={MANAGE_CUSTOM_CATEGORIES_ID.modal}
        id={'manage-categories-modal'}
        isModalOpen
        onClose={handleClose}
        withPortal
      >
        <ModalHeader data-test={MANAGE_CUSTOM_CATEGORIES_ID.header}>
          Manage custom categories
        </ModalHeader>
        <ManageCustomCategoriesForm
          fieldArray={fieldArray}
          messageCategories={messageCategories}
          onDeleteCategory={initiateCategoryDeletionProcess}
        />
        <ManageCustomCategoriesFooter
          onClose={handleClose}
          onCloseCallback={onCloseCallback}
          dirtyCategories={dirtyCategories}
          categoryToDelete={categoryToDelete}
          manageCustomCategoriesConfirmationType={manageCustomCategoriesConfirmationType}
          setManageCustomCategoriesConfirmationType={setManageCustomCategoriesConfirmationType}
          processCategoryDeletion={processCategoryDeletion}
          resetCategoryDeletionProcess={resetCategoryDeletionProcess}
        />
      </Modal>
    );
  },
);
